import axios from 'axios';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://vendor-booking-api.test/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.vendalist.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable upload progress tracking
  onUploadProgress: (progressEvent) => {
    // This will be overridden by individual requests if they provide their own
  },
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Block outgoing requests until this timestamp (ms) after a 429. */
let rateLimitUntil = 0;

/** Seconds to wait before retrying after HTTP 429 (minimum 2s; caps avoid blocking forever). */
function parseRetryAfterSeconds(response) {
  const header = response?.headers?.['retry-after'];
  if (header != null && header !== '') {
    const parsed = parseInt(String(header).trim(), 10);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      return Math.min(Math.max(parsed, 2), 120);
    }
  }
  const raw = response?.data?.retry_after;
  if (raw != null && raw !== '') {
    const parsed = typeof raw === 'number' ? raw : parseInt(String(raw).trim(), 10);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      return Math.min(Math.max(parsed, 2), 120);
    }
  }
  return 5;
}

function shouldRetry429Request(config) {
  if (!config) return false;
  const method = (config.method || 'get').toLowerCase();
  if (method === 'get' || method === 'head') return true;
  return config.retryOn429 === true;
}

function max429RetriesForRequest(config) {
  return typeof config.max429Retries === 'number' ? config.max429Retries : 1;
}

// Request interceptor to add auth token and respect client-side rate-limit cooldown
api.interceptors.request.use(
  (config) => {
    const now = Date.now();
    if (rateLimitUntil > now) {
      const waitSec = Math.ceil((rateLimitUntil - now) / 1000);
      const err = new Error(`Rate limited. Retry in ${waitSec}s.`);
      err.response = { status: 429, data: { message: 'Too many requests', retry_after: waitSec } };
      err.isRateLimited = true;
      err.config = config;
      return Promise.reject(err);
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and rate limiting
let sessionExpiredEventDispatched = false;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const res = error.response;
    const config = error.config;

    // Respect Retry-After for transient bursts. Never auto-retry mutating POST/PUT/PATCH/DELETE
    // unless the caller sets config.retryOn429 = true (idempotent / safe endpoints only).
    if (res?.status === 429 && config && shouldRetry429Request(config)) {
      const attempt = config.__retry429Count || 0;
      const maxRetries = max429RetriesForRequest(config);
      if (attempt < maxRetries) {
        const delaySec = parseRetryAfterSeconds(res);
        const jitterMs = Math.floor(Math.random() * 400);
        const delayMs = Math.min(delaySec * 1000 + jitterMs, 60000);
        await sleep(delayMs);
        config.__retry429Count = attempt + 1;
        try {
          return await api.request(config);
        } catch (retryErr) {
          error = retryErr;
        }
      }
    }

    if (error.response?.status === 429) {
      const delaySec = parseRetryAfterSeconds(error.response);
      rateLimitUntil = Date.now() + delaySec * 1000;
      error.retryAfterSeconds = delaySec;
      error.isRateLimited = true;
    }

    if (error.response?.status === 401) {
      // Token is invalid or expired
      // Don't dispatch multiple sessionExpired events
      const isLogoutRequest = error.config?.url?.includes('/auth/logout');
      
      // Skip handling if this is a logout request (logout handles its own cleanup)
      if (!isLogoutRequest && !sessionExpiredEventDispatched) {
        sessionExpiredEventDispatched = true;
        localStorage.removeItem('token');
        
        // Only redirect if not already on login/register page
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/register')) {
          const onVerification = window.location.pathname.includes('/vendor/verification');
          window.dispatchEvent(new CustomEvent('sessionExpired', {
            detail: {
              message: onVerification
                ? 'Your session timed out while verifying. Sign in again to finish — your progress was saved.'
                : 'Your session has expired. Please sign in again.',
              returnTo: window.location.pathname + window.location.search,
            }
          }));
        }
        
        // Reset flag after a delay
        setTimeout(() => {
          sessionExpiredEventDispatched = false;
        }, 2000);
      }
    } else if (
      error.response?.status === 403 &&
      (error.response?.data?.requires_subscription || error.response?.data?.subscription_expired)
    ) {
      window.dispatchEvent(
        new CustomEvent('vendorSubscriptionRequired', {
          detail: {
            message:
              error.response?.data?.message ||
              'Your subscription has ended. Please upgrade your plan.',
          },
        })
      );
    } else if (error.response?.status === 403 && error.response?.data?.message?.includes('Account is deactivated')) {
      // Vendor suspended or account deactivated - log them out immediately
      if (!sessionExpiredEventDispatched) {
        sessionExpiredEventDispatched = true;
        localStorage.removeItem('token');
        
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/register')) {
          window.dispatchEvent(new CustomEvent('sessionExpired', {
            detail: { message: error.response?.data?.error || 'Your account has been suspended. Please contact support.' }
          }));
        }
        
        setTimeout(() => {
          sessionExpiredEventDispatched = false;
        }, 2000);
      }
    }
    return Promise.reject(error);
  }
);

// Cache for getCurrentUser to prevent multiple calls
let currentUserPromise = null;

export const authService = {
  // Login user
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data; // Return the data directly
  },

  verifyTwoFactor: async ({ challenge_token, code }) => {
    const response = await api.post('/auth/two-factor/verify', { challenge_token, code });
    return response.data;
  },

  // Register user
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data; // Return the data directly
  },

  // Get current user with caching to prevent multiple simultaneous calls
  getCurrentUser: async () => {
    if (currentUserPromise) {
      return currentUserPromise;
    }

    currentUserPromise = api
      .get('/auth/me')
      .then((response) => {
        currentUserPromise = null;
        return response.data.data;
      })
      .catch((err) => {
        currentUserPromise = null;
        throw err;
      });

    return currentUserPromise;
  },

  // Refresh token
  refreshToken: async () => {
    const response = await api.post('/auth/refresh');
    return response;
  },

  /** Permanently close account (soft-delete). Password omitted when user has no local password (e.g. Google-only). */
  deleteAccount: async ({ password, reason }) => {
    const payload = {};
    if (reason) payload.reason = reason;
    if (password != null && String(password).trim() !== '') {
      payload.password = password;
    }
    const response = await api.post('/user/profile/delete', payload);
    return response.data;
  },

  // Logout user
  logout: async () => {
    // Check if token exists before making the API call
    const token = localStorage.getItem('token');
    if (!token) {
      // No token, nothing to logout
      return { data: { success: true, message: 'Already logged out' } };
    }
    
    try {
      const response = await api.post('/auth/logout');
      return response;
    } catch (error) {
      // If it's a 401, the token is already invalid, so we can consider it logged out
      if (error.response?.status === 401) {
        return { data: { success: true, message: 'Token already invalid' } };
      }
      throw error;
    }
  },

  // Forgot password
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response;
  },

  // Reset password
  resetPassword: async (token, password, passwordConfirmation, email) => {
    const response = await api.post('/auth/reset-password', { 
      token, 
      password, 
      password_confirmation: passwordConfirmation,
      email 
    });
    return response;
  },

  // Update profile
  updateProfile: async (userData) => {
    const response = await api.put('/user/profile', userData);
    return response;
  },

  // Change password
  changePassword: async (passwordData) => {
    const response = await api.post('/user/profile/change-password', {
      current_password: passwordData.current_password ?? passwordData.currentPassword,
      new_password: passwordData.new_password ?? passwordData.newPassword,
      new_password_confirmation:
        passwordData.new_password_confirmation ??
        passwordData.confirmPassword ??
        passwordData.newPasswordConfirmation,
    });
    return response;
  },

  // OAuth login/register
  oauthLogin: async (oauthData) => {
    const response = await api.post('/auth/oauth', oauthData);
    return response.data;
  },
};

export default api;
